import "babel-polyfill"
import { Map, Set } from "immutable"
import 'lazysizes'
import 'lazysizes/plugins/print/ls.print'
import 'picturefill'
import 'picturefill/dist/plugins/mutation/pf.mutation.min'
import * as React from "react"
import * as ReactDOM from "react-dom"
import { Route, Router, Switch } from "react-router-dom"
import "url-search-params-polyfill"
import "whatwg-fetch"
import { Region, getRegions } from "../../custom_api/api"
import { setLibrary } from "../../libraries"
import { SearchModal } from "../search/searchModal"
import { SearchPageState, initialState as initialSearchPageState, searchResultsRoute } from "../search/searchPage/searchPageState"
import { SearchPageWidget, newLoadingQuery } from "../search/searchPage/searchPageWidget"
import { FramedCookiebar } from "./cookiebar"
import { getCurrentFilterParams, setFilterParam } from "./filters"
import { Footer } from "./footer"
import { Header } from "./header"
import { history } from "./history"
import { Main } from "./library/main"
import { trackActivitySelection } from "./library/tracking"
import { PageRenderer } from "./page_renderer"
import { PeriodSlug } from "./region_and_date_filter"
import { ApiResult } from "../../../Shared/api/apiTypes"
import { setFeatures } from "../../../Shared/features"
import { LocalizationState, initialLocalizationState } from "../../../Shared/components/Jumbo/BranchLocalization/BranchLocalizationState"
import { getCookie } from "../../../Shared/utils/cookies"
import { PREF_BRANCH_COOKIE } from "../../../Shared/components/Jumbo/BranchLocalization/BranchLocalizationUtils"
import { async } from "widgets-for-react"
import * as DrupalModel from '../../../Shared/api/drupal/drupalTypes'
import { scrollToTop } from "../../../Shared/utils/browser"
import { CookieStatus, getCookieStatus } from "../../../Shared/utils/datalayer.mapping"

interface ApplicationProps { }

export interface ApplicationState {
  readonly routes: Set<string>
  readonly regions: ApiResult<Map<string, Region>>
  readonly cookieStatus: CookieStatus
  readonly showArchive: boolean
  readonly searchModalOpen: boolean
  readonly searchPageState: SearchPageState
  readonly localizationState: LocalizationState
}

const applicationStateZero: ApplicationState = {
  routes: Set(["/", "/nieuwsbrief"]),
  regions: "loading",
  cookieStatus: getCookieStatus(),
  showArchive: false,
  searchModalOpen: false,
  searchPageState: initialSearchPageState(),
  localizationState: initialLocalizationState,
}

export const drupalHomepagePath = '/home'

class Application extends React.Component<ApplicationProps, ApplicationState> {
  constructor(props: ApplicationProps) {
    super(props)

    this.state = applicationStateZero

    this.selectRegion = this.selectRegion.bind(this)
    this.selectPeriod = this.selectPeriod.bind(this)
    this.resetPeriod = this.resetPeriod.bind(this)
    this.selectLocation = this.selectLocation.bind(this)
  }

  componentDidMount() {
    const s = this.state

    addEventListener('popstate', scrollToTop);

    if (s.regions == "loading") {
      getRegions().then(r => this.setState((s, _) => ({ ...s, regions: r })))
    }
  }

  componentWillUnmount() {
    removeEventListener('popstate', scrollToTop);
  }

  selectRegion(e: React.ChangeEvent<HTMLSelectElement>) {
    e.stopPropagation()
    setFilterParam('waar', e.target.value)
    trackActivitySelection("where", e.target.value)
  }

  selectPeriod(e: React.ChangeEvent<HTMLSelectElement>) {
    e.stopPropagation()
    setFilterParam('wanneer', e.target.value)
  }

  resetPeriod() {
    setFilterParam('wanneer', 'komend halfjaar')
  }

  selectLocation(selectedLocationId: string) {
    this.setState((s0) => ({ ...s0, localizationState: { ...s0.localizationState, selectedLocationId: selectedLocationId } }))
  }

  render() {
    const regions: Map<string, Region> =
      this.state.regions == "loading" || this.state.regions == "error" 
        ? Map<string, Region>()
        : this.state.regions

    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
    const branchLocations = this.state.localizationState.branchLocations.kind == 'loaded' ? this.state.localizationState.branchLocations.value : []
    const cookieUpdater = (cookieStatus: CookieStatus) => this.setState(s => ({ ...s, cookieStatus }))

    const pageRenderer = (slug: string, params?: string) => {
      let filterParams = getCurrentFilterParams()

      return (
        <>
          {async<DrupalModel.Location[]>()(this.state.localizationState.branchLocations).run(u => this.setState((s0) => (
              { ...s0, localizationState: { ...s0.localizationState, branchLocations: u(s0.localizationState.branchLocations) } }
            )))}
          <PageRenderer
            route={slug}
            params={params}
            regions={regions}
            selectedPeriod={filterParams.has('wanneer') ? filterParams.get('wanneer') as PeriodSlug: 'komend-halfjaar'}
            selectedRegion={filterParams.has('waar') ? filterParams.get('waar'): ''}
            selectedCluster={filterParams.has('categorie') ? filterParams.get('categorie'): ''}
            selectPeriod={this.selectPeriod}
            selectRegion={this.selectRegion}
            resetPeriod={this.resetPeriod}
            toggleArchive={cb => this.setState(s => ({ ...s, showArchive: !s.showArchive }), () => (cb ? cb() : null))}
            showArchive={this.state.showArchive}
            cookieUpdater={cookieUpdater}
            localizationProps={({
              selectedLocationId: this.state.localizationState.selectedLocationId,
              branchLocations: branchLocations,
              selectLocation: this.selectLocation
            })}
          />
        </>
      )
    }

    const branchId = getCookie(PREF_BRANCH_COOKIE)
    const homePath = (branchId && branchId != "0") ? `/node/${branchId}` : drupalHomepagePath

    return (
      <Router history={history}>
        <>
          <Header searchModalOpen={this.state.searchModalOpen} toggleSearchModal={() => this.setState(s => ({ ...s, searchModalOpen: !s.searchModalOpen }), () => {
              document.documentElement.classList.toggle('search--open')
              if (this.state.searchModalOpen) document.getElementById('site-search-modal').focus()
            })} />
          <SearchModal
            isOpen={this.state.searchModalOpen}
            toggleSearchModal={() => this.setState(s => ({ ...s, searchModalOpen: !s.searchModalOpen }), () => document.documentElement.classList.toggle('search--open'))}
            setSearchPageState={(newState, cb) => this.setState(s => ({ ...s, searchPageState: { ...s.searchPageState, ...newState } }), cb)}
            runNewQuery={(searchQuery) => {
              history.push(searchResultsRoute)
              this.setState(
                (s0) => ({
                  ...s0,
                  searchModalOpen: false,
                  searchPageState: newLoadingQuery(searchQuery, s0.searchPageState)
                }),
                () => document.documentElement.classList.toggle('search--open'))
              }
            }
          />
          <Main>
            <Switch>
              <Route exact key="home" path="/" render={props => pageRenderer(homePath)} />
              <Route exact key="search" path={searchResultsRoute} render={_ =>
                SearchPageWidget({ ...this.state.searchPageState })
                  .run((searchPageState) => this.setState((prevState) => ({...prevState, searchPageState})))
              }
              />
              <Route exact key="page" path="/:slug*" render={props => pageRenderer(props.location.pathname, props.location.search)} />
            </Switch>
          </Main>
          <Footer onCookieChange={cookieUpdater} />
          <FramedCookiebar
            status={this.state.cookieStatus}
            onChange={cookieUpdater}
          />
        </>
      </Router>
    )
  }
}

export const render: (library: string) => void = (l) => {
  setLibrary(l)
  setFeatures(l)
  ReactDOM.render(
    <Application/>, 
    document.getElementById("react-content"), 
    () => {removeLoader()}
  )
}

function removeLoader() {
  const loader = document.documentElement.querySelector('#js--loader');
  if(loader == null) return;
  loader.remove();
}

{/* TH: Added to styleguide! */}
